import type { SwadeModConfigurationInterface, SwadeModificationInterface } from '@/types/items';
import type { SwadePowerContainerInterface, SwadePowerLevelInterface } from '@/types/powers';
import { isOfferedOption, supportedContainerPath } from '@/appdata/container-targets';
import { isSkillRankEffect, wholeNumber } from '@/appdata/skill-rank-effects';

/**
 * Pure helpers over one power container (spec 2026-09-21 §5.3, §5.4, §5.6). Nothing here reads the
 * store or writes the container. `levelCount` has a PHP twin, PowerContainer::getLevelCount(): the
 * same rule in two languages (ruling 21) — change both in one cycle.
 */

/** What newPowerContainer writes; every container persisted before this cycle has no schema key. */
export const POWER_CONTAINER_SCHEMA = 2;

/** Ruling 20: the marker, not the copy list, is the discriminator. Anything unparseable is legacy. */
export const isLegacyPowerContainer = (power: SwadePowerContainerInterface | null | undefined): boolean => {
    const schema = Number(power?.configuration?.schema ?? 1);
    return !Number.isFinite(schema) || schema < POWER_CONTAINER_SCHEMA;
};

/**
 * How many levels of an "X/level" power the container holds: basePowerPoints over the per-level cost
 * newPowerContainer stored in configuration.multiplier, at least 1. A table power, a null switch, a
 * missing or non-positive multiplier and a textual cost all answer 1.
 */
export function levelCount(power: SwadePowerContainerInterface | null | undefined): number {
    const details = power?.selection?.details as { allowMultiple?: boolean | null } | null | undefined;
    if (details?.allowMultiple !== true) return 1;
    const multiplier = Number(power?.configuration?.multiplier);
    const base = Number(power?.basePowerPoints);
    if (!Number.isFinite(multiplier) || multiplier <= 0 || power?.basePowerPoints === null || !Number.isFinite(base)) return 1;
    return Math.max(1, Math.round(base / multiplier));
}

/** The only name the store uses for the level count: how many times a power's authored effects apply. */
export const grantMultiplier = (power: SwadePowerContainerInterface | null | undefined): number => levelCount(power);

/** The catalog's direct grants: every modification that is neither an offered set nor a choice set. */
export function directGrants(power: SwadePowerContainerInterface | null | undefined): SwadeModificationInterface[] {
    const modifications = power?.selection?.modifications;
    if (!Array.isArray(modifications)) return [];
    return modifications.filter((mod) =>
        !!mod?.configuration
        && !isOfferedOption(mod.configuration)
        && mod.configuration.path !== 'character.choiceSet');
}

/**
 * The catalog's own (non-offered) choice sets — Reach's "Reach 1/2/3" — answered when the power is added,
 * exactly as an edge's or an item's are (owner, 2026-09-24). An OFFERED set is a modifier the player picks
 * on the card and is never prompted here. Kept apart from directGrants, which the level ticks walk: a
 * choice is made once, not once per level.
 */
export function catalogChoiceSets(power: SwadePowerContainerInterface | null | undefined): SwadeModificationInterface[] {
    const modifications = power?.selection?.modifications;
    if (!Array.isArray(modifications)) return [];
    return modifications.filter((mod) =>
        !!mod?.configuration
        && mod.configuration.path === 'character.choiceSet'
        && !isOfferedOption(mod.configuration));
}

/**
 * A grant whose target the player picks on the card (spec 2026-09-21 §5.4): its element is blank —
 * Super Attribute's '' or Super Skill's null. It applies nothing on its own; its pick is a record.
 *
 * An ABSENT element (`undefined`) is ambiguous by path, and the two paths disagree on purpose. A
 * `character.*` grant has no container to fall back on, so an absent element still means "the
 * player must pick one" — a target grant. A `container.*` grant names self mode instead
 * (container-targets.ts's `isPromptElement`/self-mode split: `undefined` there means "the container
 * that granted me"), which resolves on its own without asking anybody anything — a FIXED grant, not
 * a target grant. `''`, `null` and `[]` stay target grants on every path.
 */
export const isTargetGrant = (configuration: SwadeModConfigurationInterface | null | undefined): boolean => {
    const element = configuration?.element;
    if (element === undefined) return supportedContainerPath(configuration?.path) === undefined;
    return element === null || element === '' || (Array.isArray(element) && element.length === 0);
};

/** The catalog's fixed grants: the direct grants that name their target and so apply on their own, once per level. */
export const fixedGrants = (power: SwadePowerContainerInterface | null | undefined): SwadeModificationInterface[] =>
    directGrants(power).filter((mod) => !isTargetGrant(mod.configuration));

/**
 * The catalog's container picks (owner, 2026-09-25): a direct `container.*` grant whose target the
 * player picks — a prompt element or an empty list, never self mode (absent element) nor All Specified
 * (a listed target). The card's pickers cover the three character paths alone, so these are prompted
 * when the power is added, as an edge's or an item's are, recorded on the power, applied once, re-targeted
 * through the card's pencil, and rolled back with the power. Each comes at the power's level
 * (levelScaledConfiguration), so a pick recorded at any level carries the value for that level.
 */
export const containerTargetGrants = (power: SwadePowerContainerInterface | null | undefined): SwadeModificationInterface[] =>
    directGrants(power)
        .filter((mod) => supportedContainerPath(mod.configuration.path) !== undefined && isTargetGrant(mod.configuration))
        .map((mod) => {
            const configuration = levelScaledConfiguration(power, mod.configuration);
            return configuration === mod.configuration ? mod : { ...mod, configuration };
        });

/**
 * A container pick at the power's level (owner, 2026-10-08): a skill RANK change carries its value times
 * the level count, so level 2 makes `add 1 modify` an `add 2 modify`. Any other configuration, and a value
 * that is not a whole number, is returned as it is. Only a super power has levels (levelCount), so a
 * standard power's pick is never scaled.
 */
export function levelScaledConfiguration(power: SwadePowerContainerInterface | null | undefined, configuration: SwadeModConfigurationInterface): SwadeModConfigurationInterface {
    const value = isSkillRankEffect(configuration) ? wholeNumber(configuration.value) : null;
    if (value === null) return configuration;
    const scaled = value * grantMultiplier(power);
    return scaled === configuration.value ? configuration : { ...configuration, value: scaled };
}

/** Whether a container sits in character.powers, by identity or by uuid. */
export const isPowerOf = (character: any, container: any): boolean =>
    Array.isArray(character?.powers)
    && character.powers.some((p: any) => p === container || (typeof container?.uuid === 'string' && p?.uuid === container.uuid));

export interface PowerLevelOption {
    key: string;
    basePowerPoints: number;
    cost: number;
    /** The level count for a linear power; null for a table row. */
    count: number | null;
    /** The table row for a table power; null for a linear count. */
    level: SwadePowerLevelInterface | null;
}

/**
 * What the level menu offers (spec §6 row 2): the counts 1..maxSelections (2 when uncapped, as the
 * card's menu has always defaulted) for a linear power, or the catalog rows for a table power. Empty
 * when the cost is textual.
 */
export function levelOptions(power: SwadePowerContainerInterface | null | undefined): PowerLevelOption[] {
    const details = power?.selection?.details as { allowMultiple?: boolean | null, maxSelections?: number | null, levels?: SwadePowerLevelInterface[] } | null | undefined;
    if (details?.allowMultiple === true) {
        const multiplier = Number(power?.configuration?.multiplier);
        if (!Number.isFinite(multiplier) || multiplier <= 0) return [];
        const cap = Number.isInteger(details.maxSelections) && (details.maxSelections as number) >= 1 ? details.maxSelections as number : 2;
        return Array.from({ length: cap }, (_, i) => ({ key: `count-${i + 1}`, basePowerPoints: (i + 1) * multiplier, cost: (i + 1) * multiplier, count: i + 1, level: null }));
    }
    const rows = Array.isArray(details?.levels) ? details.levels : [];
    return rows
        .map((level, index) => ({ key: `row-${index}`, basePowerPoints: Number(level?.powerPoints), cost: Number(level?.powerPoints), count: null, level }))
        .filter((option) => Number.isFinite(option.basePowerPoints));
}
